/**
 * Platform Quota Catalog — Limits & Quotas (Platform Configuration)
 *
 * This is NOT the Tenant Limits editor (that lives on each tenant's detail
 * page under the "Limits" tab, where per-tenant overrides are set). This
 * page shows the platform-wide CATALOG of quota types themselves: what
 * MAX_USERS, MAX_STORAGE_GB, etc. are, what they default to, and where a value
 * for a given tenant actually comes from.
 *
 * It deliberately does NOT claim these are enforced. They are not: the
 * enforcement functions for this vocabulary have no callers, and the page said
 * otherwise until the quota convergence audit. `enforcement_type` is rendered
 * as the recorded intention it is.
 *
 * Reuses GET /api/admin/control-plane/limits/standard — the same endpoint
 * and STANDARD_LIMITS catalog that already drives the Tenant Limits tab and
 * the Plan default-limits editor. No new backend logic was added for this
 * page; it is a read-only view of existing data.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../auth/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/table';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Gauge,
  Loader2,
  RefreshCw,
  Info,
  Infinity as InfinityIcon,
  CreditCard,
  Building2,
  ArrowRight,
  ShieldAlert,
  ShieldQuestion,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CONTROL_PLANE_API = `${BACKEND_URL}/api/admin/control-plane`;

// Category keys as returned by STANDARD_LIMITS -> a friendlier display label.
// Purely cosmetic formatting of existing data — not a new taxonomy.
const CATEGORY_LABELS = {
  users: 'Users',
  storage: 'Storage',
  schema: 'Schema (Custom Objects & Fields)',
  automation: 'Automation & Flows',
  api: 'API',
  ai: 'AI',
  forms: 'Forms',
  documents: 'Documents',
};

// Every standard limit uses the same platform-wide "-1 = unlimited" sentinel
// (enforced universally in TenantLimitsService.check_limit), not a per-key
// flag from the backend — so this is a constant, not a fetched field.
const UNLIMITED_SENTINEL_SUPPORTED = true;

const deriveUnit = (limitKey) => (limitKey.endsWith('_GB') ? 'GB' : 'count');

const RESET_CYCLE_LABELS = {
  NEVER: 'Does not reset',
  DAILY: 'Resets daily',
  WEEKLY: 'Resets weekly',
  MONTHLY: 'Resets monthly',
  YEARLY: 'Resets yearly',
};

const QuotaCatalogPage = () => {
  const navigate = useNavigate();
  const { getAdminToken } = useAdminAuth();
  const [limits, setLimits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStandardLimits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${CONTROL_PLANE_API}/limits/standard`, {
        headers: { Authorization: `Bearer ${getAdminToken()}` },
      });
      setLimits(response.data.limits || []);
    } catch (err) {
      console.error('Failed to fetch standard limits catalog:', err);
      setError('Could not load the quota catalog');
      toast.error('Failed to load quota catalog');
    } finally {
      setLoading(false);
    }
  }, [getAdminToken]);

  useEffect(() => {
    fetchStandardLimits();
  }, [fetchStandardLimits]);

  const grouped = limits.reduce((groups, def) => {
    const cat = def.category || 'general';
    (groups[cat] = groups[cat] || []).push(def);
    return groups;
  }, {});

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Gauge className="h-6 w-6 text-indigo-600" />
            Limits &amp; Quotas
          </h1>
          <p className="text-slate-500 mt-1">
            The platform-wide catalog of every quota type — what each one means, its default,
            and how it's enforced. This is not where per-tenant values are set.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchStandardLimits} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {/* Explains the read-only nature and where the editable controls actually live */}
      <Card className="border-indigo-100 bg-indigo-50/50">
        <CardContent className="py-4">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-700 space-y-2">
              <p>
                This catalog (<code className="text-xs bg-white px-1 py-0.5 rounded border">STANDARD_LIMITS</code>)
                is compiled into the backend, not stored in the database — so it can't be edited from this
                page. A given tenant's actual ceiling for a quota is resolved at runtime in this order:{' '}
                <strong>Standard catalog default</strong> (shown below) →{' '}
                <strong>the tenant's plan</strong> (if it configures that key) →{' '}
                <strong>a tenant-specific override</strong> (if one has been set).
              </p>
              <p>
                Every quota below supports an unlimited state through the universal{' '}
                <code className="text-xs bg-white px-1 py-0.5 rounded border">-1</code> sentinel, which the
                resolution logic understands platform-wide rather than per quota type.
              </p>
              <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                <strong>These ceilings are recorded, not enforced.</strong> The enforcement functions for
                these keys exist but have no callers in either repository, and the consumption counter
                behind them has never moved on any tenant. A value set here is a recorded, audited
                intention — it does not stop a tenant from exceeding it. Live enforcement today runs only
                in Data Manager, against a separate set of quota keys that are not in this catalog. See{' '}
                <strong>Quota Reconciliation</strong> for the per-key breakdown.
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" variant="outline" className="bg-white" onClick={() => navigate('/subscriptions')}>
                  <CreditCard className="h-3.5 w-3.5 mr-2" />
                  Configure Plan Defaults
                  <ArrowRight className="h-3.5 w-3.5 ml-2" />
                </Button>
                <Button size="sm" variant="outline" className="bg-white" onClick={() => navigate('/tenants')}>
                  <Building2 className="h-3.5 w-3.5 mr-2" />
                  Manage Tenant Overrides
                  <ArrowRight className="h-3.5 w-3.5 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
        </div>
      ) : error ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <ShieldAlert className="h-8 w-8 text-red-400 mb-3" />
            <p className="text-slate-600">{error}</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={fetchStandardLimits}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : limits.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <ShieldQuestion className="h-8 w-8 text-slate-300 mb-3" />
            <p className="text-slate-600">No standard quota definitions were returned by the backend.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([category, defs]) => (
            <Card key={category} data-testid={`quota-category-${category}`}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  {CATEGORY_LABELS[category] || category}
                </CardTitle>
                <CardDescription>
                  Consumed by: {CATEGORY_LABELS[category] || category} — {defs.length} quota
                  {defs.length === 1 ? '' : 's'} in this category
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Default Value</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead>Reset Cycle</TableHead>
                        <TableHead>Enforcement intent</TableHead>
                        <TableHead>Unlimited</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {defs.map((def) => (
                        <TableRow key={def.limit_key} data-testid={`quota-row-${def.limit_key}`}>
                          <TableCell>
                            <div className="font-medium text-slate-800">{def.name}</div>
                            <div className="text-xs text-slate-400 font-mono">{def.limit_key}</div>
                          </TableCell>
                          <TableCell className="text-slate-600 max-w-xs">{def.description}</TableCell>
                          <TableCell className="font-semibold text-slate-900 tabular-nums">
                            {Number(def.default_value).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-slate-500">{deriveUnit(def.limit_key)}</TableCell>
                          <TableCell className="text-slate-500">
                            {RESET_CYCLE_LABELS[def.reset_cycle] || def.reset_cycle}
                          </TableCell>
                          <TableCell>
                            {/* Rendered as the recorded intention it is. A red
                                `destructive` badge reading "Hard Stop" under a
                                column headed "Enforcement" was the strongest
                                claim on the page, and it was false — nothing
                                calls the functions that would stop anything. */}
                            <Badge variant="outline" className="font-normal text-slate-600" title="Recorded intention. Not enforced today — no code path checks these ceilings.">
                              {def.enforcement_type === 'HARD_STOP' ? 'Hard Stop (intended)' : 'Soft Warning (intended)'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {UNLIMITED_SENTINEL_SUPPORTED && (
                              <Badge variant="secondary" className="gap-1">
                                <InfinityIcon className="h-3 w-3" />
                                Supported
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuotaCatalogPage;
